/**
 * Asks iOS to line the fixed frame back up with the screen after the
 * on-screen keyboard closes. Mounted once, from GlobalChrome, right beside
 * useFabViewportTracking.
 *
 * THE REPORT (Bobbie's iPhone, Hub on the home screen, 2026-10-03 about
 * 9:06pm Central, the Messages inbox): the Back pill sat about 59% of the
 * way down the screen, over a card's green "Cover it" button, with the
 * Assistant bubble and the jump-to-top arrow at the same height. The fixed
 * breadcrumb strip at the top was gone entirely. Measured from her
 * screenshot, EVERY fixed element had moved up by about one keyboard
 * height (roughly 380 CSS px).
 *
 * WHY THIS IS NOT A SIXTH PLACEMENT FIX. Nothing we run can put the Back
 * pill there: .ldg-fab-layer rests at `bottom: 0` in CSS and
 * fabViewportTracking.ts can lift it 80px at most. And nothing we run
 * touches the top strip at all. When BOTH move together, the browser's own
 * frame for position:fixed (the layout viewport) is offset from what is on
 * screen, which is what visualViewport.offsetTop measures. The known
 * WebKit pattern, worst in home screen web apps: focusing a field scrolls
 * the layout viewport to show it, and after the keyboard goes away iOS
 * sometimes does not scroll it back, so fixed elements stay shifted until
 * something makes WebKit re-sync. That is a HYPOTHESIS until her phone
 * says otherwise. docs/lessons.md ("A repeat UI bug on her device") is why
 * this file never places anything from viewport numbers.
 *
 * WHAT THIS DOES: one scroll call that requests the current position,
 * `window.scrollTo(window.scrollX, window.scrollY)`, and only after the
 * keyboard has closed and only when the frame is still offset. On a device
 * the content may shift to meet the frame: whether iOS reports
 * window.scrollY from the visual or the layout origin in this state is
 * unproven, so this does not claim the call moves nothing there. It never
 * writes a position, top, height or transform. The worst a wrong reading
 * can cause is one scroll request to the position the page itself reports.
 *
 * WHY THAT EXACT CALL (WebKit main, read 2026-10-04, not proven on her
 * iOS version): the two-number form is a non-animated programmatic scroll
 * (index.css sets no `scroll-behavior`). ScrollView::setScrollPosition only
 * returns early on "no change" for a USER scroll, so a programmatic one to
 * the same spot still goes through. The web process applies it at once,
 * and the UI process, in WKWebView's _scrollToContentScrollPosition, finds
 * the offset unchanged and calls resendLastVisibleContentRects, whose own
 * comment is "To synchronize the WebProcess with what is on screen, we
 * send the VisibleContentRect again." That re-send is the re-sync we want.
 * Two limits read from the same source: LocalDOMWindow::scrollTo skips a
 * scroll to 0,0 when already at 0,0, so at the very top of a page this
 * call does nothing (the Director's screen check shows scrollY, so that
 * case will be visible); and scrollBy or setting scrollTop take the same
 * path, so neither is a better no-op. The object form with
 * `behavior: 'instant'` is avoided on purpose: an engine that does not know
 * that value throws.
 *
 * WHEN IT RUNS, and nothing else:
 *   - `focusout` from a text entry field, checked SETTLE_MS later, and only
 *     if no text entry field has focus by then.
 *   - the keyboard closing: a visualViewport `resize` that reaches within
 *     KEYBOARD_CLOSED_SLACK_PX of window.innerHeight after a keyboard-sized
 *     reading was seen, also checked SETTLE_MS later. A LATCH remembers the
 *     keyboard-sized reading, because iOS can close the keyboard over
 *     several resizes (600, 700, 760, 800, 830, 844 on a 844 screen), and
 *     comparing each resize only with the one before it lost the
 *     keyboard-sized start, so a gradual close never scheduled a check (PR
 *     #1320 review). The latch is set by any reading at least
 *     KEYBOARD_OPEN_MIN_GAP_PX short of innerHeight, the reading at mount
 *     included (the hook can mount with the keyboard up), held for as long
 *     as the readings stay keyboard-sized (typing can take minutes, and focus
 *     moving between fields keeps the keyboard up, so focus does NOT clear
 *     it), held through the part-way readings of a close, and cleared in
 *     exactly two ways: reaching the closed height, which schedules ONE
 *     check, or a close that stalls part-way with no further resize for
 *     KEYBOARD_CLOSE_STALL_MS, which schedules nothing (the viewport never
 *     closed). A keyboard-sized reading AT MOUNT also starts that stall
 *     timer, because no resize has yet shown the keyboard is really up: if
 *     neither a keyboard-sized resize (which cancels the timer, as above)
 *     nor a close arrives within KEYBOARD_CLOSE_STALL_MS, the latch drops,
 *     so a stuck mount reading cannot hold it forever. The cost: a hook that
 *     mounts with the keyboard up and then sees no resize at all for that
 *     long loses the latch, and only the focusout check is left for that
 *     close. Two more ways the latch drops, from the third review: a check
 *     that finds the keyboard closed drops it, so a closing resize that
 *     arrives AFTER that check cannot schedule a second one (one close, one
 *     nudge at most); and a reading whose innerHeight has moved more than
 *     LATCH_LAYOUT_SLACK_PX from the innerHeight the latch was set on drops
 *     it (a rotation can change innerHeight with no resize, which left the
 *     latch to fire on a toolbar resize seconds later). The cost of the
 *     second: if innerHeight itself moves during a close, only the focusout
 *     check covers that close. Unmounting drops the latch too. A reading
 *     that never was keyboard-sized (Safari's toolbar) cannot set it.
 *   NEVER on a visualViewport `scroll` event: this file does not listen to
 *   one. A scroll call during momentum scrolling sends the page back to
 *   wherever the web process last thought it was, which stops the fling.
 *
 * GATES at check time, all required: the same plausibility gate the lift
 * uses (isPlausibleViewportReading: finite numbers, scale within
 * SCALE_TOLERANCE of 1), offsetTop above NUDGE_MIN_OFFSET_PX, no text entry
 * field focused, the keyboard looking closed (so this never runs in the
 * middle of iOS's own dismiss animation), and the page not scrolling right
 * now. A check that finds the page still moving tries ONCE more, SETTLE_MS
 * later. One trigger is one check: a newer trigger replaces a pending
 * check rather than adding a second, and the scroll call itself changes no
 * focus and no viewport height, so it cannot trigger another one. No loops.
 *
 * TEMPORARY PART: onResyncCheck and the ResyncCheck event exist only so
 * the Director's screen check (components/ScreenCheckReadout.tsx) can say
 * whether a nudge ran. Remove them with that readout once the cause is
 * confirmed. The nudge itself stays if it proves to be the fix.
 */
import { useEffect } from 'react';
import { isPlausibleViewportReading, SCALE_TOLERANCE } from './fabViewportTracking';

/** How long after the trigger the check waits for iOS to settle. */
export const SETTLE_MS = 300;
/** visualViewport.height within this many px of innerHeight reads as "keyboard closed". */
export const KEYBOARD_CLOSED_SLACK_PX = 4;
/**
 * A reading at least this far short of innerHeight is keyboard-sized and
 * sets the latch (see the file comment). Larger than Safari's toolbar
 * collapse and smaller than any iPhone keyboard, so a toolbar animation
 * mid-scroll is never mistaken for one.
 */
export const KEYBOARD_OPEN_MIN_GAP_PX = 120;
/**
 * A close that has started (the height grew back from keyboard size) but
 * has had no resize for this long without reaching the closed height drops
 * the latch with no check. iOS's own dismiss animation is well under this.
 */
export const KEYBOARD_CLOSE_STALL_MS = 2000;
/**
 * The latch belongs to the innerHeight it was set on. A later reading
 * whose innerHeight differs by more than this (a rotation, or a frame that
 * resized with no visualViewport resize) drops it, so it cannot fire
 * against a frame it never measured.
 */
export const LATCH_LAYOUT_SLACK_PX = 4;
/** offsetTop at or below this is lined up; nothing to re-sync. */
export const NUDGE_MIN_OFFSET_PX = 1;
/** A window scroll event this recent means the page is still moving. */
export const RECENT_SCROLL_MS = 120;

export interface ViewportReading {
  /** window.innerHeight */
  layoutHeight: number;
  height: number;
  offsetTop: number;
  scale: number | undefined;
}

export function readViewport(vv: VisualViewport): ViewportReading {
  return {
    layoutHeight: window.innerHeight,
    height: vv.height,
    offsetTop: vv.offsetTop,
    scale: vv.scale,
  };
}

// Input types that never bring up the keyboard. Everything else on an
// <input> does (text, search, email, number, tel, url, password, and the
// date and time pickers, which also take over the bottom of the screen).
const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

/** A field that brings up the on-screen keyboard: textarea, a text-like input, or contenteditable. */
export function isTextEntryElement(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(target.type);
  return target.isContentEditable;
}

export function textEntryHasFocus(): boolean {
  return isTextEntryElement(document.activeElement);
}

export function keyboardLooksClosed(reading: ViewportReading): boolean {
  return reading.height >= reading.layoutHeight - KEYBOARD_CLOSED_SLACK_PX;
}

/** Keyboard-sized: at least KEYBOARD_OPEN_MIN_GAP_PX short of innerHeight. Sets the latch. */
export function keyboardLooksOpen(reading: ViewportReading): boolean {
  return (
    Number.isFinite(reading.height) &&
    Number.isFinite(reading.layoutHeight) &&
    reading.layoutHeight - reading.height >= KEYBOARD_OPEN_MIN_GAP_PX
  );
}

/** A finite scale further than SCALE_TOLERANCE from 1: the page is zoomed. */
export function isZoomedScale(scale: number | undefined): boolean {
  return typeof scale === 'number' && Number.isFinite(scale) && Math.abs(scale - 1) > SCALE_TOLERANCE;
}

export type NudgeSkipReason = 'zoomed' | 'implausible' | 'typing' | 'no-offset' | 'keyboard-open' | 'moving';

/** Null means every gate passed and the nudge runs. */
export function nudgeSkipReason(
  reading: ViewportReading,
  typing: boolean,
  pageMoving: boolean
): NudgeSkipReason | null {
  const { layoutHeight, height, offsetTop, scale } = reading;
  if (isZoomedScale(scale)) return 'zoomed';
  if (!isPlausibleViewportReading(layoutHeight, height, offsetTop, scale)) return 'implausible';
  if (typing) return 'typing';
  if (!(offsetTop > NUDGE_MIN_OFFSET_PX)) return 'no-offset';
  if (!keyboardLooksClosed(reading)) return 'keyboard-open';
  if (pageMoving) return 'moving';
  return null;
}

export type ResyncTrigger = 'focusout' | 'keyboard-closed';

/** TEMPORARY, for the Director's screen check only. See the file comment. */
export interface ResyncCheck {
  trigger: ResyncTrigger;
  nudged: boolean;
  skipped: NudgeSkipReason | null;
  at: number;
}

const checkListeners = new Set<(check: ResyncCheck) => void>();

/** TEMPORARY, for the Director's screen check only. Returns the unsubscribe. */
export function onResyncCheck(listener: (check: ResyncCheck) => void): () => void {
  checkListeners.add(listener);
  return () => {
    checkListeners.delete(listener);
  };
}

export function useViewportResync(): void {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let pending: number | undefined;
    // The latch: a keyboard-sized reading was seen and the viewport has not
    // reached the closed height since. Sampled at mount, because
    // GlobalChrome mounts behind auth and user-doc readiness and can mount
    // (or remount) with the keyboard still up; starting false there lost
    // the close (PR #1320 review). See the file comment.
    const mountReading = readViewport(vv);
    let keyboardSeenOpen = keyboardLooksOpen(mountReading);
    // The innerHeight the latch was set on (see LATCH_LAYOUT_SLACK_PX).
    let latchLayoutHeight = mountReading.layoutHeight;
    let stall: number | undefined;
    let lastWindowScrollAt = Number.NEGATIVE_INFINITY;

    const clearStall = () => {
      if (stall !== undefined) window.clearTimeout(stall);
      stall = undefined;
    };
    const dropLatch = () => {
      keyboardSeenOpen = false;
      clearStall();
    };
    // Drops the latch with no check if nothing moves it for
    // KEYBOARD_CLOSE_STALL_MS (see the file comment).
    const armStall = () => {
      clearStall();
      stall = window.setTimeout(() => {
        stall = undefined;
        keyboardSeenOpen = false;
      }, KEYBOARD_CLOSE_STALL_MS);
    };
    // A latch set on another innerHeight is not this frame's (rotation).
    const dropLatchIfFrameMoved = (reading: ViewportReading) => {
      if (keyboardSeenOpen && !(Math.abs(reading.layoutHeight - latchLayoutHeight) <= LATCH_LAYOUT_SLACK_PX)) {
        dropLatch();
      }
    };

    const runCheck = (trigger: ResyncTrigger, retryIfMoving: boolean) => {
      pending = undefined;
      const reading = readViewport(vv);
      dropLatchIfFrameMoved(reading);
      // A check that finds the keyboard closed has handled this close: a
      // closing resize that arrives after it must not schedule a second
      // check (one close, one nudge at most).
      if (keyboardLooksClosed(reading)) dropLatch();
      const pageMoving = Date.now() - lastWindowScrollAt < RECENT_SCROLL_MS;
      const skipped = nudgeSkipReason(reading, textEntryHasFocus(), pageMoving);
      if (skipped === 'moving' && retryIfMoving) {
        pending = window.setTimeout(() => runCheck(trigger, false), SETTLE_MS);
        return;
      }
      if (skipped === null) {
        // The re-sync. Requests the current position; on a device the
        // content may shift to meet the frame. See the file comment for why
        // this form and what it asks WebKit to do.
        window.scrollTo(window.scrollX, window.scrollY);
      }
      const check: ResyncCheck = { trigger, nudged: skipped === null, skipped, at: Date.now() };
      checkListeners.forEach((listener) => listener(check));
    };

    const schedule = (trigger: ResyncTrigger) => {
      if (pending !== undefined) window.clearTimeout(pending);
      pending = window.setTimeout(() => runCheck(trigger, true), SETTLE_MS);
    };

    const onFocusOut = (event: FocusEvent) => {
      if (isTextEntryElement(event.target)) schedule('focusout');
    };

    // Resize only. There is deliberately no visualViewport 'scroll'
    // listener in this file (see the file comment).
    const onViewportResize = () => {
      const reading = readViewport(vv);
      dropLatchIfFrameMoved(reading);
      if (keyboardLooksOpen(reading)) {
        keyboardSeenOpen = true;
        latchLayoutHeight = reading.layoutHeight;
        clearStall();
        return;
      }
      if (!keyboardSeenOpen) return;
      if (keyboardLooksClosed(reading)) {
        // Closed after a keyboard-sized reading: ONE check, latch cleared.
        dropLatch();
        schedule('keyboard-closed');
        return;
      }
      // Part way back: keep the latch, unless the close stalls here.
      armStall();
    };

    // Timestamp only, passive: tells a check that the page is mid-scroll.
    const onWindowScroll = () => {
      lastWindowScrollAt = Date.now();
    };

    // Mounted with the keyboard up: the latch is set, on the stall clock.
    if (keyboardSeenOpen) armStall();

    document.addEventListener('focusout', onFocusOut);
    vv.addEventListener('resize', onViewportResize);
    window.addEventListener('scroll', onWindowScroll, { passive: true });
    return () => {
      document.removeEventListener('focusout', onFocusOut);
      vv.removeEventListener('resize', onViewportResize);
      window.removeEventListener('scroll', onWindowScroll);
      if (pending !== undefined) window.clearTimeout(pending);
      clearStall();
    };
  }, []);
}
